// ===========================================================================
// Deduction postes — shared by `EstimationCard` (« Nouvelle estimation ») and
// its twin `EstimationRecoursCard` (« Estimation recours »).
// ---------------------------------------------------------------------------
// A deduction poste (Franchise, Valeur de sauvetage…) is SUBTRACTED from the
// card total, and it must carry a NEGATIVE amount end to end (mahhp 22851):
// VEOS only deducts it when the provision is written with the minus sign. The
// cards therefore force the sign while the user types, display a legacy
// positive stored amount as negative, compute the total on the magnitude, and
// send `mnt` negative.
// ===========================================================================

/**
 * Default `deductionPostes` (CD_PSD codes) — the sign is forced out of the box,
 * tenants can still `setProp` the list.
 *
 * - `ASSFRC` / `ERVSRC` — the codes VEOS actually returns for « Franchise » /
 *   « Valeur de sauvetage » on both `WS2_GS_TAB_ESTIMATIONS_ALL` and
 *   `WS2_GS_TAB_ESTIMATIONS_RECOURS_ALL` (IGA SERVICING Postman captures).
 * - `FRAN` / `SAUV` — the codes documented for `iga-servicing` in
 *   `.claude-work/specs/estimation-card.md` (Open Question 1).
 *
 * A module-level constant so the default keeps a stable identity across
 * renders (the cards memoise their lookup set on it). Never mutated.
 */
export const DEFAULT_DEDUCTION_POSTES: string[] = ['FRAN', 'SAUV', 'ASSFRC', 'ERVSRC'];

/**
 * Force the deduction sign on a raw amount string. A value that parses to a
 * non-zero finite number becomes `-|n|`, keeping the user's digits / decimals
 * verbatim (`'1000'` → `'-1000'`, `'12.50'` → `'-12.50'`, `'+5'` → `'-5'`); an
 * already-negative value is returned unchanged. Anything else — `''`, `'-'`,
 * `'0'`, `'0.00'`, partial or non-numeric input — is returned as typed so the
 * user can keep typing.
 */
export function toDeductionAmount(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return value;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return value;
  return `-${trimmed.replace(/^\+/, '')}`;
}

/** The amount as displayed / compared: forced negative for a deduction poste. */
export function signedAmountString(value: string, isDeduction: boolean): string {
  return isDeduction ? toDeductionAmount(value) : value;
}

/**
 * `-|n|` for the save payload, without producing `-0` (a cleared / zero
 * deduction poste is sent as a plain `0`).
 */
export function negativeMagnitude(n: number): number {
  return n === 0 ? 0 : -Math.abs(n);
}

/**
 * The amount a poste is SAVED with (provision / prevision POST `mnt`): a
 * deduction poste is written negative (`-|n|`, never `-0`), any other poste as
 * typed. The `WS2_GS_VER_ESTIMATION` pre-check (mahhp 22858) forwards this same
 * value, so VEOS checks the amounts exactly as they will be written.
 */
export function savedPosteAmount(value: string, isDeduction: boolean): number {
  const n = Number(value);
  return isDeduction ? negativeMagnitude(n) : n;
}

/**
 * MINIMUM floor (montant déjà réglé). A regular poste keeps the signed check
 * (`value < MINIMUM` ⇒ error). A deduction poste is forced negative, so its
 * floor applies to the MAGNITUDE (`|value| < |MINIMUM|` ⇒ error) — a signed
 * check against a positive floor would reject every forced-negative amount.
 */
export function isBelowMinimum(value: string, minimum: number, isDeduction: boolean): boolean {
  const n = Number(value);
  return isDeduction ? Math.abs(n) < Math.abs(minimum) : n < minimum;
}
